/**
 * Redux slice для управления уведомлениями
 * 
 * Отвечает за:
 * - Хранение состояния уведомлений
 * - Предоставление действий для работы с уведомлениями
 * - Обработку статусов загрузки и ошибок
 */
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { apiService } from '../../api/api.service';
import type { INotification } from '../../types';

export interface NotificationPayload {
    title: string;
    body: string;
    type: string;
    language: string;
    scheduled_at?: string;
    consider_timezone?: boolean;
    active_subscriptions?: boolean;
    inactive_subscriptions?: boolean;
    is_active: boolean;
    data?: {
        screen?: string;
        tab?: string;
        external_link?: string;
        language?: string;
        params?: Record<string, any>;
    }; // Необязательное поле для навигационных данных
}

export interface NotificationsState {
    notifications: INotification[];
    loading: boolean;
    submitting: boolean;
    error: string | null;
}

const initialState: NotificationsState = {
    notifications: [],
    loading: false,
    submitting: false,
    error: null,
};

// Thunk для получения списка уведомлений
export const fetchNotifications = createAsyncThunk(
    'notifications/fetchNotifications',
    async (_, { rejectWithValue }) => {
        try {
            return await apiService.getNotifications();
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'notifications.errors.loadFailed');
        }
    }
);

// Thunk для отправки уведомления
export const sendNotification = createAsyncThunk(
    'notifications/sendNotification',
    async (payload: NotificationPayload, { rejectWithValue }) => {
        try {
            return await apiService.sendNotification(payload);
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'notifications.errors.sendFailed');
        }
    }
);

// Thunk для удаления уведомления
export const deleteNotification = createAsyncThunk(
    'notifications/deleteNotification',
    async (id: number, { rejectWithValue }) => {
        try {
            await apiService.deleteNotification(id);
            return id;
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'notifications.errors.deleteFailed');
        }
    }
);

const notificationsSlice = createSlice({
    name: 'notifications',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // fetchNotifications
            .addCase(fetchNotifications.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchNotifications.fulfilled, (state, action: PayloadAction<INotification[]>) => {
                state.notifications = action.payload;
                state.loading = false;
            })
            .addCase(fetchNotifications.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            // sendNotification
            .addCase(sendNotification.pending, (state) => {
                state.submitting = true;
                state.error = null;
            })
            .addCase(sendNotification.fulfilled, (state, action: PayloadAction<INotification>) => {
                state.notifications.unshift(action.payload);
                state.submitting = false;
            })
            .addCase(sendNotification.rejected, (state, action) => {
                state.submitting = false;
                state.error = action.payload as string;
            })

            // deleteNotification
            .addCase(deleteNotification.pending, (state) => {
                state.error = null;
            })
            .addCase(deleteNotification.fulfilled, (state, action: PayloadAction<number>) => {
                state.notifications = state.notifications.filter(
                    (notification) => notification.id !== action.payload
                );
            })
            .addCase(deleteNotification.rejected, (state, action) => {
                state.error = action.payload as string;
            });
    },
});

export const { clearError } = notificationsSlice.actions;
export default notificationsSlice.reducer; 